import _jsx from "@babel/runtime/helpers/jsx";
var _Text, _Badge;
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import clsx from 'clsx';
import { useFeatures } from '@atc/features';
import { useBrand } from '@atc/brand';
import { Badge, Glyphicon, LazyRender, List, Text } from 'reaxl';
import { KBBDealIndicatorOverlay, Pricing, ListingPaymentCTA } from 'reaxl-molecules';
import { WalletPaymentDetails, Payments } from 'reaxl-wallet';
import InventoryListingTitle from '../InventoryListingTitle/index.js';
import InventoryListingBadges from '../InventoryListingBadges/index.js';
const fordUrgencyBadge = label => {
  const color = label === 'Reduced Price' ? 'success' : 'accent';
  const dataCmp = label.replace(/\s+/g, '').charAt(0).toLowerCase() + label.slice(1);
  return /*#__PURE__*/_jsx(Text, {
    "data-cmp": dataCmp,
    weight: "bold",
    color: color,
    antialiased: true
  }, void 0, label);
};
const renderBadges = _ref => {
  let {
    isNewlyListed,
    isPreorder,
    isReducedPrice,
    isHot,
    uiContext,
    isFord
  } = _ref;
  const urgencyBadge = isHot && uiContext !== 'snapshot' && fordUrgencyBadge('High Demand') || isNewlyListed && fordUrgencyBadge('Newly Listed') || isReducedPrice && fordUrgencyBadge('Reduced Price');
  return /*#__PURE__*/_jsx("div", {
    className: "padding-bottom-1 margin-right-2"
  }, "badges", isFord && urgencyBadge, isPreorder && (_Text || (_Text = /*#__PURE__*/_jsx(Text, {
    className: "margin-right-2",
    "data-cmp": "customBuild",
    antialiased: true,
    color: "accent",
    weight: "bold"
  }, void 0, "Custom Order"))));
};
const renderMileage = mileage => /*#__PURE__*/_jsx("span", {
  className: "text-bold"
}, void 0, `${mileage.value} miles`);
const renderEstimatePayment = _ref2 => {
  let {
    listing,
    isAlpha,
    isPortrait,
    showListingPaymentRibbon,
    enableMyWallet,
    extraDealerInfo,
    renderPaymentDetails,
    onPaymentCTAClick,
    specificationKeys,
    uiContext
  } = _ref2;
  const listingPaymentRibbonClasses = 'text-right';
  if (uiContext === 'snapshot' || !specificationKeys.length) {
    return null;
  }
  if (isAlpha) {
    if (!isPortrait) {
      return extraDealerInfo;
    }
    return null;
  }
  return /*#__PURE__*/_jsx(Fragment, {}, "estimate-payment", showListingPaymentRibbon && renderPaymentDetails(listingPaymentRibbonClasses), !showListingPaymentRibbon && enableMyWallet && !listing.accelerate && /*#__PURE__*/_jsx(ListingPaymentCTA, {
    className: clsx(listingPaymentRibbonClasses, 'text-size-200'),
    onPaymentCTAClick: onPaymentCTAClick
  }, "paymentCTA"));
};
const renderSpecifications = _ref3 => {
  let {
    listing,
    specifications = {},
    orientation,
    secondaryContent,
    specificationKeys,
    uiContext,
    enableCpoVhrBadges,
    onCertifiedTileClick,
    onProductTileClick,
    isFord
  } = _ref3;
  if (uiContext === 'snapshot' || !specificationKeys.length) {
    return null;
  }
  if (uiContext === 'alpha') {
    if (orientation !== 'portrait') {
      return secondaryContent;
    }
    return null;
  }
  const hasMileage = specificationKeys.indexOf('mileage') !== -1 && specifications.mileage && specifications.mileage.value;
  return /*#__PURE__*/_jsx("div", {
    className: "row"
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: "item-card-specifications col-xs-9 margin-top-4 text-subdued-lighter"
  }, void 0, orientation === 'portrait' && hasMileage && renderMileage(specifications.mileage), /*#__PURE__*/_jsx(InventoryListingBadges, {
    enableCertifiedBadges: !isFord,
    enableCpoVhrBadges: enableCpoVhrBadges,
    listing: listing,
    onCertifiedTileClick: onCertifiedTileClick,
    onProductTileClick: onProductTileClick
  })));
};
const renderPricing = _ref4 => {
  let {
    isPreorder,
    onPricingDetailClick,
    orientation,
    payments,
    paymentsCTA,
    pricingDetail,
    showPayments,
    showPaymentDetailsLink,
    isPortrait,
    isAlpha,
    listing,
    renderPaymentDetails,
    showListingPaymentRibbon,
    enableMyWallet,
    onPaymentCTAClick,
    personalPaymentProps,
    extraDealerInfo,
    specificationKeys,
    uiContext
  } = _ref4;
  const pricingContainerClasses = clsx('margin-left-auto', showPayments ? 'col-xs-4 text-right pull-right' : 'col-xs-12 text-left');
  const alphaPaymentRibbonClasses = 'margin-bottom-2 text-right';
  const pricingContext = showPayments ? 'listing' : 'details';
  const pricingUiContext = uiContext === 'recommended' ? 'default' : pricingContext;
  const renderPayments = /*#__PURE__*/React.createElement(Payments, Object.assign({
    key: "inventory-listing-payments",
    listingId: listing.id,
    payments: payments,
    showPaymentDetailsLink: showPaymentDetailsLink,
    personalPaymentProps: personalPaymentProps
  }, paymentsCTA));
  const renderPricingComponent = /*#__PURE__*/_jsx(Pricing, {
    data: pricingDetail,
    isPreorder: isPreorder,
    uiContext: pricingUiContext,
    onPricingDetailClick: onPricingDetailClick,
    orientation: orientation
  }, "inventory-listing-pricing");
  return /*#__PURE__*/_jsx("div", {
    className: pricingContainerClasses
  }, void 0, uiContext !== 'recommended' && renderPricingComponent, !showListingPaymentRibbon && /*#__PURE__*/_jsx(Fragment, {}, "payments", renderPayments), showListingPaymentRibbon && isAlpha && isPortrait && renderPaymentDetails(alphaPaymentRibbonClasses), !showListingPaymentRibbon && enableMyWallet && isAlpha && isPortrait && !listing.accelerate && /*#__PURE__*/_jsx(ListingPaymentCTA, {
    className: clsx(alphaPaymentRibbonClasses, 'text-size-200'),
    onPaymentCTAClick: onPaymentCTAClick
  }), renderEstimatePayment({
    listing,
    isAlpha,
    isPortrait,
    showListingPaymentRibbon,
    enableMyWallet,
    extraDealerInfo,
    renderPaymentDetails,
    onPaymentCTAClick,
    specificationKeys,
    uiContext
  }), uiContext === 'recommended' && renderPricingComponent);
};
const renderListingBodyContent = _ref5 => {
  let {
    listing,
    listingCTA = {},
    onPricingDetailClick,
    orientation,
    paymentsCTA,
    secondaryContent,
    dynamicSpecifications,
    specificationKeys,
    uiContext,
    showPaymentDetailsLink,
    onProductTileClick,
    onProductTileLinkClick,
    onSaveToggle,
    isActiveDeal,
    isSaved,
    showPackages,
    showListingPaymentRibbon,
    enableMyWallet,
    onMyWalletClick,
    personalPaymentProps,
    updateListingPayments,
    onPaymentCTAClick,
    extraDealerInfo,
    panelHeaderText,
    enableCpoVhrBadges,
    onCertifiedTileClick,
    isFord,
    onContextMenu,
    ...rest
  } = _ref5;
  const {
    isNewlyListed,
    isReducedPrice,
    isLessMileage,
    isHot,
    pricingDetail = {},
    title,
    specifications,
    accelerate
  } = listing;
  const isSnapshot = uiContext === 'snapshot';
  const isAlpha = uiContext === 'alpha';
  const isPreorder = uiContext === 'preorder';
  const isPortrait = orientation === 'portrait';
  const renderPaymentDetails = className => !isActiveDeal && /*#__PURE__*/_jsx(LazyRender, {}, "paymentDetails", /*#__PURE__*/_jsx("div", {
    className: className
  }, void 0, /*#__PURE__*/_jsx(WalletPaymentDetails, {
    listingId: listing.id,
    isPreorder: isPreorder,
    onMyWalletClick: onMyWalletClick,
    personalPaymentProps: personalPaymentProps,
    uiContext: uiContext === 'recommended' ? 'default' : 'ribbon',
    updateListingPayments: updateListingPayments
  })));
  const titleProps = {
    ...listingCTA,
    // let top level title prop override the supplied listingCTA prop
    title: title || listingCTA.title,
    size: isPortrait ? 300 : 500,
    onContextMenu
  };
  return /*#__PURE__*/React.createElement("div", Object.assign({
    className: clsx({
      'padding-left-3': uiContext === 'snapshot' || orientation === 'portrait'
    }, 'inventory-listing-body padding-top-3 padding-right-3 margin-bottom-2'),
    role: "link",
    tabIndex: 0
  }, rest), uiContext === 'recommended' && /*#__PURE__*/_jsx("div", {
    className: "row"
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: "col-xs-12 margin-bottom-1"
  }, void 0, /*#__PURE__*/_jsx(Text, {
    size: 400,
    color: "blue-darker",
    weight: "bold"
  }, void 0, panelHeaderText))), /*#__PURE__*/_jsx("div", {
    className: "row"
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: clsx('text-left', isSnapshot ? 'col-xs-12' : 'col-xs-8')
  }, void 0, (isNewlyListed || isPreorder || isReducedPrice || isHot) && renderBadges({
    isNewlyListed,
    isPreorder,
    isReducedPrice,
    isHot,
    uiContext,
    isFord
  }), /*#__PURE__*/_jsx("div", {
    className: "display-flex justify-content-between"
  }, "title", /*#__PURE__*/React.createElement(InventoryListingTitle, titleProps)), dynamicSpecifications?.length > 0 && /*#__PURE__*/_jsx(List, {
    "data-cmp": "dynamicSpecificationsList",
    items: dynamicSpecifications,
    inline: true,
    className: clsx('margin-top-2 text-size-200')
  }), !isActiveDeal && renderSpecifications({
    specifications,
    orientation,
    secondaryContent,
    specificationKeys,
    uiContext,
    listing,
    enableCpoVhrBadges,
    onCertifiedTileClick,
    onProductTileClick,
    isFord
  })), renderPricing({
    isPreorder,
    listing,
    payments: pricingDetail.payment,
    paymentsCTA,
    pricingDetail,
    onPricingDetailClick,
    orientation,
    showPayments: !isSnapshot,
    showPaymentDetailsLink,
    accelerate,
    isPortrait,
    isAlpha,
    renderPaymentDetails,
    showListingPaymentRibbon,
    enableMyWallet,
    onPaymentCTAClick,
    personalPaymentProps,
    extraDealerInfo,
    specificationKeys,
    uiContext
  })), isLessMileage && uiContext === 'snapshot' && (_Badge || (_Badge = /*#__PURE__*/_jsx(Badge, {
    className: "display-inline-flex gap-1 align-items-baseline text-normal text-size-200 margin-top-3 warning"
  }, void 0, /*#__PURE__*/_jsx(Glyphicon, {
    glyph: "mileage",
    className: "warning text-size-300"
  }), "Less Mileage"))));
};
const renderListingBody = _ref6 => {
  let {
    listing,
    listingCTA,
    onPricingDetailClick,
    orientation,
    paymentsCTA,
    secondaryContent,
    dynamicSpecifications,
    specificationKeys,
    uiContext,
    showPaymentDetailsLink,
    onProductTileClick,
    onProductTileLinkClick,
    onSaveToggle,
    isActiveDeal,
    isSaved,
    showListingPaymentRibbon,
    showKBBIndicator,
    enableMyWallet,
    onMyWalletClick,
    personalPaymentProps,
    onPaymentCTAClick,
    extraDealerInfo,
    isFord,
    enableCpoVhrBadges,
    onCertifiedTileClick,
    onContextMenu,
    ...rest
  } = _ref6;
  const {
    pricingDetail = {}
  } = listing;
  const includeKBBIndicator = showKBBIndicator && pricingDetail.dealIndicator && orientation === 'portrait';
  const component = includeKBBIndicator ? KBBDealIndicatorOverlay : 'div';
  const props = includeKBBIndicator && {
    pricingDetail
  };
  const listingBody = renderListingBodyContent({
    listing,
    listingCTA,
    onPricingDetailClick,
    orientation,
    paymentsCTA,
    secondaryContent,
    dynamicSpecifications,
    specificationKeys,
    uiContext,
    showPaymentDetailsLink,
    onProductTileClick,
    onProductTileLinkClick,
    onSaveToggle,
    isActiveDeal,
    isSaved,
    showListingPaymentRibbon,
    enableMyWallet,
    onMyWalletClick,
    personalPaymentProps,
    onPaymentCTAClick,
    extraDealerInfo,
    isFord,
    enableCpoVhrBadges,
    onCertifiedTileClick,
    onContextMenu,
    ...rest
  });
  return /*#__PURE__*/React.createElement(component, props, listingBody);
};

/**
 *  Describe the component
 *
 */
function InventoryListingBody(_ref7) {
  let {
    dynamicSpecifications,
    enableMyWallet = false,
    extraDealerInfo,
    isActiveDeal,
    isSaved,
    listing,
    listingCTA,
    onCertifiedTileClick,
    onContextMenu,
    onMyWalletClick,
    onPaymentCTAClick,
    onPricingDetailClick,
    onProductTileClick,
    onProductTileLinkClick,
    onSaveToggle,
    orientation,
    paymentsCTA,
    personalPaymentProps = {},
    secondaryContent,
    showKBBIndicator = true,
    showListingPaymentRibbon = false,
    showPackages = true,
    showPaymentDetailsLink,
    specificationKeys = ['color', 'mileage', 'mpg', 'driveType', 'engine'],
    uiContext,
    ...rest
  } = _ref7;
  const {
    enable_cpo_vhr_badges: [, {
      show_cpo_vhr_badges: enableCpoVhrBadges
    }]
  } = useFeatures(['enable_cpo_vhr_badges']);
  const {
    isBrand
  } = useBrand();
  const isFord = isBrand('ford');
  return renderListingBody({
    listing,
    listingCTA,
    onPricingDetailClick,
    orientation,
    paymentsCTA,
    secondaryContent,
    dynamicSpecifications,
    specificationKeys,
    uiContext,
    showPaymentDetailsLink,
    onProductTileClick,
    onProductTileLinkClick,
    onSaveToggle,
    isActiveDeal,
    isSaved,
    showPackages,
    showListingPaymentRibbon,
    showKBBIndicator,
    enableMyWallet,
    onMyWalletClick,
    personalPaymentProps,
    onPaymentCTAClick,
    extraDealerInfo,
    isFord,
    enableCpoVhrBadges,
    onCertifiedTileClick,
    onContextMenu,
    ...rest
  });
}
export default InventoryListingBody;